/**
 * Money formatting.
 *
 * The TCO screens were rendering figures as raw literals ("€ 229,67") and
 * elsewhere as `${value.toFixed(2)} €`, which puts the symbol on the wrong side
 * for fr-FR and ignores thousands separators entirely -- 12345.6 came out as
 * "12345.60 €" instead of "12 345,60 €".
 *
 * Intl handles separator, symbol placement and rounding per locale, so callers
 * pass a number and get the right string for the active language.
 */

/** Locale used when i18n has not resolved one yet. */
const FALLBACK_LOCALE = "fr-FR";

const resolveLocale = (locale?: string): string =>
  locale && locale.trim().length > 0 ? locale : FALLBACK_LOCALE;

/**
 * Format a euro amount.
 *
 * Null, undefined and NaN all collapse to zero rather than "—": on a cost
 * dashboard an absent figure means nothing was spent, and a dash reads like a
 * loading failure.
 */
export const formatEuros = (
  value: number | null | undefined,
  locale?: string
): string => {
  const numeric = typeof value === "number" && Number.isFinite(value) ? value : 0;

  return new Intl.NumberFormat(resolveLocale(locale), {
    style: "currency",
    currency: "EUR",
  }).format(numeric);
};

/**
 * Same, rounded to whole euros. For headline tiles where cents are noise.
 */
export const formatEurosCompact = (
  value: number | null | undefined,
  locale?: string
): string => {
  const numeric = typeof value === "number" && Number.isFinite(value) ? value : 0;

  return new Intl.NumberFormat(resolveLocale(locale), {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numeric);
};
